import { useMemo, useRef, type JSX, type RefObject } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command.js';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover.js';

import {
  COMPOSER_POPOVER_LIST_HEIGHT,
  useScrollHighlightIntoView,
} from './composer-popover-list.js';
import type { MentionEntry, MentionSection } from './mention-entry.js';

interface MentionMenuProps {
  /** Mirrors `useComposer`'s `mentionState !== null && mentionState.open`. */
  open: boolean;
  /** The rows to list, already grouped and ordered by their sources. */
  sections: MentionSection[];
  /** True while a source may still answer — decides what an empty list says. */
  searching: boolean;
  /** The highlighted row's key, or null for "the first row". */
  highlightKey: string | null;
  /** Fired when the highlight should move — cmdk calls it on mouse hover over a row. */
  onHighlightChange: (key: string) => void;
  onSelect: (entry: MentionEntry) => void;
  /** Fired when the popover wants to close (outside click) — wired to the composer's Escape dismissal. */
  onDismiss: () => void;
  /**
   * The composer textarea the popup hangs under. It is anchored BY REF rather than by wrapping the
   * textarea the way the slash popup does, because the textarea can only have one wrapper: swapping it
   * between two of them would remount the textarea and take the user's focus and caret with it.
   */
  anchorRef: RefObject<HTMLTextAreaElement | null>;
}

/**
 * The `@` popup: the files a message can reference, from every source that has any, as a cmdk `Command`
 * list inside a Radix `Popover`.
 *
 * It is driven entirely by `useComposer`'s mention state and knows nothing about where a row came from.
 * A row is an entry — a name, a detail, an icon and an action of its own — and the sections it is grouped
 * into are what say which source answered. That is also what keeps the list still while it fills: a source
 * that answers late drops its rows into its own section, so nothing already on screen moves.
 *
 * Keyboard focus never leaves the textarea, exactly as in `SlashCommandMenu`, so cmdk's own key handling
 * is unreachable and `Command`'s controlled `value` is used instead: it mirrors the highlighted row's key,
 * and `onValueChange` reports a hover back. Enter is handled in `useComposer.onKeyDown`.
 */
export function MentionMenu({
  open,
  sections,
  searching,
  highlightKey,
  onHighlightChange,
  onSelect,
  onDismiss,
  anchorRef,
}: MentionMenuProps): JSX.Element {
  const entries = sections.flatMap((section) => section.entries);
  /**
   * What the popup measures itself against. It is built here rather than handing Radix the textarea's own
   * ref because that ref holds `null` until the node mounts, and an anchor that may be null is not the
   * anchor Radix's type asks for — a mismatch whose visibility depends on the exact `@types/react` patch
   * installed, so it passed one typecheck and failed the build's. Measuring through a getter keeps the
   * popup on the live textarea; the empty rect only ever applies before the node exists, when the popup
   * is closed anyway.
   */
  const anchor = useMemo(
    () => ({
      current: {
        getBoundingClientRect: (): DOMRect =>
          anchorRef.current?.getBoundingClientRect() ?? new DOMRect(),
      },
    }),
    [anchorRef],
  );
  /** No explicit highlight means the first row, which is also the row Enter picks. */
  const highlighted = highlightKey ?? entries[0]?.key ?? '';

  const listRef = useRef<HTMLDivElement>(null);
  useScrollHighlightIntoView(listRef, open, highlighted);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onDismiss();
      }}
    >
      <PopoverAnchor virtualRef={anchor} />
      <PopoverContent
        align="start"
        sideOffset={4}
        collisionPadding={8}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        className="w-80 p-1"
      >
        <Command
          shouldFilter={false}
          value={highlighted}
          onValueChange={(value) => {
            const hovered = entries.find((entry) => entry.key === value);
            if (hovered) onHighlightChange(hovered.key);
          }}
        >
          <CommandList ref={listRef} className={COMPOSER_POPOVER_LIST_HEIGHT}>
            {entries.length === 0 ? (
              <CommandEmpty>
                {searching ? 'Searching SharePoint…' : 'No matching files'}
              </CommandEmpty>
            ) : (
              sections.map((section) => (
                <CommandGroup key={section.kind} heading={section.heading}>
                  {section.entries.map((entry) => (
                    <CommandItem
                      key={entry.key}
                      value={entry.key}
                      aria-label={entry.accessibleName}
                      onSelect={() => onSelect(entry)}
                    >
                      <entry.icon
                        className="size-4 shrink-0 text-muted-foreground"
                        aria-hidden
                      />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate">{entry.label}</span>
                        {entry.detail ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {entry.detail}
                          </span>
                        ) : null}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
